/**
 * AI Chat Mention System - Context Builder
 *
 * Builds AI context from resolved mentions for intelligent component generation.
 * Handles data context (schemas/fields) and page context.
 */

import { schemaApi, type Schema, type SchemaField } from '@/services/api/schemaApi';
import type { ResolvedMentionData } from './types';

// ============================================================================
// Types
// ============================================================================

/**
 * Schema information for AI context
 */
export interface SelectedSchema {
  /** Schema ID */
  id: string;
  /** Schema name (machine-readable) */
  name: string;
  /** Schema display name (human-readable) */
  displayName: string;
  /** Schema description */
  description?: string;
  /** Schema fields */
  fields: SchemaField[];
}

/**
 * Field information for AI context
 */
export interface SelectedField {
  /** Schema ID the field belongs to */
  schemaId: string;
  /** Schema name (machine-readable) */
  schemaName: string;
  /** Schema display name (human-readable) */
  schemaDisplayName: string;
  /** Field information */
  field: SchemaField;
}

/**
 * Data context for AI - includes schemas and fields from @mentions
 */
export interface DataContext {
  /** Full schema information for mentioned schemas */
  selectedSchemas: SelectedSchema[];
  /** Specific field references from mentions */
  selectedFields: SelectedField[];
}

/**
 * Page context for AI - includes page references from #mentions
 */
export interface PageContext {
  /** Pages referenced in mentions */
  pages: Array<{
    id: string;
    name: string;
    isDefault?: boolean;
  }>;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Maximum number of schemas to include in context
 * Prevents overwhelming the AI with too much schema data
 */
const MAX_SCHEMA_CONTEXT = 5;

// ============================================================================
// Functions
// ============================================================================

/**
 * Build data context from resolved mentions
 *
 * Collects unique schemas and fields from data mentions,
 * fetches full schema details, and returns structured context for AI.
 *
 * @param resolvedMentions - Array of resolved mention data
 * @returns DataContext with schemas and fields, or null if no data mentions
 *
 * @example
 * const dataContext = await buildDataContext(resolvedMentions);
 * // dataContext.selectedSchemas contains full schema info
 * // dataContext.selectedFields contains specific field references
 */
export async function buildDataContext(
  resolvedMentions: (ResolvedMentionData | null)[]
): Promise<DataContext | null> {
  // Filter to data mentions only
  const dataMentions = resolvedMentions.filter(
    (m): m is ResolvedMentionData => m !== null && m.type === 'data'
  );

  if (dataMentions.length === 0) {
    return null;
  }

  // Collect unique schema IDs using Set
  const schemaIds = new Set<string>();
  dataMentions.forEach((mention) => {
    if (mention.schema?.id) {
      schemaIds.add(mention.schema.id);
    }
  });

  // Limit to MAX_SCHEMA_CONTEXT schemas
  const schemaIdArray = Array.from(schemaIds);
  if (schemaIdArray.length > MAX_SCHEMA_CONTEXT) {
    console.warn(
      `[MentionContextBuilder] Too many schemas referenced (${schemaIdArray.length}). Limiting to ${MAX_SCHEMA_CONTEXT}.`
    );
  }
  const limitedSchemaIds = schemaIdArray.slice(0, MAX_SCHEMA_CONTEXT);

  // Fetch full schema details in parallel
  const schemaPromises = limitedSchemaIds.map(async (schemaId) => {
    try {
      return await schemaApi.getSchema(schemaId);
    } catch (error) {
      console.error(
        `[MentionContextBuilder] Failed to fetch schema ${schemaId}:`,
        error
      );
      return null;
    }
  });

  const schemas = await Promise.all(schemaPromises);
  const validSchemas = schemas.filter((s): s is Schema => s !== null);

  // Build selected schemas array
  const selectedSchemas: SelectedSchema[] = validSchemas.map((schema) => ({
    id: schema.id,
    name: schema.name,
    displayName: schema.displayName,
    description: schema.description,
    fields: schema.fields || [],
  }));

  // Extract specific field references from mentions
  const selectedFields: SelectedField[] = [];
  dataMentions.forEach((mention) => {
    if (mention.field && mention.schema) {
      // Find the full schema to get complete field info
      const fullSchema = validSchemas.find((s) => s.id === mention.schema?.id);
      if (fullSchema) {
        const fullField = fullSchema.fields?.find(
          (f) => f.name === mention.field?.name
        );
        if (fullField) {
          selectedFields.push({
            schemaId: mention.schema.id,
            schemaName: mention.schema.name,
            schemaDisplayName: mention.schema.displayName,
            field: fullField,
          });
        }
      }
    }
  });

  return {
    selectedSchemas,
    selectedFields,
  };
}

/**
 * Build page context from resolved mentions
 *
 * Collects unique pages from page mentions and returns structured context.
 *
 * @param resolvedMentions - Array of resolved mention data
 * @returns PageContext with page info, or null if no page mentions
 *
 * @example
 * const pageContext = buildPageContext(resolvedMentions);
 * // pageContext.pages contains page ID, name, and isDefault flag
 */
export function buildPageContext(
  resolvedMentions: (ResolvedMentionData | null)[]
): PageContext | null {
  // Filter to page mentions only
  const pageMentions = resolvedMentions.filter(
    (m): m is ResolvedMentionData => m !== null && m.type === 'page' && !!m.page
  );

  if (pageMentions.length === 0) {
    return null;
  }

  // Deduplicate pages by ID using Map
  const pageMap = new Map<
    string,
    { id: string; name: string; isDefault?: boolean }
  >();

  pageMentions.forEach((mention) => {
    if (mention.page && !pageMap.has(mention.page.id)) {
      pageMap.set(mention.page.id, {
        id: mention.page.id,
        name: mention.page.name,
        isDefault: mention.page.isDefault,
      });
    }
  });

  return {
    pages: Array.from(pageMap.values()),
  };
}
